import React, { useEffect, useMemo, useState } from "react";
import Link from "shared/Link";
import Card from "shared/Card";
import { Section } from "shared/layout";
import LoadingContainer from "shared/LoadingContainer";
import Table from "shared/Table";
import Input from "shared/@forms/Input";
import styles from "./index.module.css";

import { useEmailLookup } from "./useEmailLookup";
import { useIBOInfo } from "../LookupByIBO/useIBOInfo";
import { useQueries } from "react-query";
import { Auth } from "shared/@auth/Auth";

function extractIboInfo(ibo) {
  const number =
    ibo?.iboNumber ??
    ibo?.wwgInfo?.iboNumber ??
    ibo?._id ??
    ibo?.id ??
    "unknown";

  const name =
    ibo?.iboInfo?.name ||
    ibo?.fullName ||
    (ibo?.wwgInfo?.firstName || ibo?.wwgInfo?.lastName
      ? `${ibo.wwgInfo.firstName ?? ""} ${ibo.wwgInfo.lastName ?? ""}`.trim()
      : `${ibo?.firstName ?? ""} ${ibo?.lastName ?? ""}`.trim());

  return { number, name };
}

function getIboEmails(ibo) {
  const candidates = [
    ibo?.email,
    ibo?.wwgInfo?.email,
    ibo?.iboInfo?.email,
    ibo?.contactEmail,
    ibo?.user?.email,
  ];
  return Array.from(
    new Set(
      candidates.filter(Boolean).map((e) => String(e).trim().toLowerCase())
    )
  );
}

function exactMatchIfPresent(items = [], selectedEmail) {
  if (!selectedEmail) return items;
  return items.filter((ibo) => {
    const emails = getIboEmails(ibo);
    return emails.length > 0 && emails.includes(selectedEmail);
  });
}

function pickEmailFromInfo(iboInfo) {
  if (!iboInfo) return null;
  const primary =
    iboInfo.email ||
    iboInfo.primaryEmail ||
    iboInfo.contactEmail ||
    iboInfo?.user?.email ||
    iboInfo?.wwgInfo?.email ||
    iboInfo?.iboInfo?.email ||
    null;
  if (primary) return String(primary).trim().toLowerCase();

  const emailRe = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i;
  const stack = [iboInfo];
  const seen = new Set();
  while (stack.length) {
    const cur = stack.pop();
    if (!cur || typeof cur !== "object" || seen.has(cur)) continue;
    seen.add(cur);
    for (const k in cur) {
      const v = cur[k];
      if (typeof v === "string") {
        const m = v.match(emailRe);
        if (m) return m[0].toLowerCase();
      } else if (v && typeof v === "object") {
        stack.push(v);
      }
    }
  }
  return null;
}

function WWGItem({ ibo, selectedEmail }) {
  const { number, name } = extractIboInfo(ibo);
  const normalizedSelected = (selectedEmail || "").toLowerCase();

  const { iboInfo, isLoading, isError } = useIBOInfo(number);
  if (isLoading || isError) return null;

  const derivedEmail = pickEmailFromInfo(iboInfo);
  if (!derivedEmail || derivedEmail !== normalizedSelected) return null;

  return (
    <div>
      <Link to={`/membership/lookup/ibo/${number}`}>
        {name ? `${name} (${number})` : `(${number})`}
      </Link>{" "}
      {ibo.iboLabel && (
        <strong className={styles.iboLabel}>
          &nbsp;— IBO&nbsp;{ibo.iboLabel}
        </strong>
      )}
    </div>
  );
}

export default function LookupByEmail() {
  const [editTerm, setEditTerm] = useState("");
  const [submittedEmail, setSubmittedEmail] = useState(null);

  const isLikelyEmail = useMemo(() => {
    const t = editTerm.trim().toLowerCase();
    return t.length >= 3 && t.includes("@");
  }, [editTerm]);

  const { data, isLoading: lookupLoading } = useEmailLookup(submittedEmail);

  const filteredDreamStream = exactMatchIfPresent(
    data?.dreamStream,
    submittedEmail
  );
  const filteredTopics = exactMatchIfPresent(data?.topics, submittedEmail);

  const { makeAuthedRequest } = Auth.useContainer();

  const wwgNumbers = useMemo(() => {
    const wwg = Array.isArray(data?.wwg) ? data.wwg : [];
    return Array.from(
      new Set(
        wwg
          .map((ibo) =>
            String(
              ibo?.iboNumber ??
                ibo?.wwgInfo?.iboNumber ??
                ibo?._id ??
                ibo?.id ??
                ""
            ).trim()
          )
          .filter(Boolean)
      )
    );
  }, [data?.wwg]);

  const wwgQueries = useQueries(
    (wwgNumbers || []).map((num) => ({
      queryKey: ["ibo", num],
      queryFn: async () => {
        const res = await makeAuthedRequest(`/v1/membership/ibo/${num}`);
        if (!res.ok) {
          try {
            const body = await res.json();
            throw new Error(body?.message || `IBO ${num} -> ${res.status}`);
          } catch {
            throw new Error(`IBO ${num} -> ${res.status}`);
          }
        }
        return res.json();
      },
      enabled: Boolean(submittedEmail) && Boolean(num),
      staleTime: 5 * 60 * 1000,
    }))
  );

  const allWwgDone =
    wwgQueries.length === 0 ||
    wwgQueries.every((q) => q.isSuccess || q.isError);

  const isLoadingCombined =
    Boolean(submittedEmail) && (lookupLoading || !allWwgDone);

  const iboInfoByNumber = useMemo(() => {
    const map = new Map();
    wwgNumbers.forEach((num, idx) => {
      map.set(num, wwgQueries[idx]?.data ?? null);
    });
    return map;
  }, [wwgNumbers, wwgQueries]);

  const matchedWWG = useMemo(() => {
    if (!Array.isArray(data?.wwg)) return [];
    const normalized = (submittedEmail || "").toLowerCase();
    return data.wwg.filter((ibo) => {
      const { number } = extractIboInfo(ibo);
      const info = iboInfoByNumber.get(String(number).trim());
      const email = pickEmailFromInfo(info);
      return Boolean(email && email === normalized);
    });
  }, [data?.wwg, iboInfoByNumber, submittedEmail]);

  const onSubmit = (e) => {
    e.preventDefault();
    const next = editTerm.trim().toLowerCase();
    if (next.length >= 3 && next.includes("@")) {
      setSubmittedEmail(next);
    } else {
      setSubmittedEmail(null);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.searchBarWrapper}>
        <form onSubmit={onSubmit} className={styles.searchBar}>
          <div className={styles.inputGroup}>
            <Input
              value={editTerm}
              onChange={(e) => setEditTerm(e.target.value)}
              placeholder="Type an e-mail address"
              autoFocus
              className={styles.inputField}
            />
            <button
              type="submit"
              className={styles.searchButton}
              disabled={!isLikelyEmail}
              aria-label="Search by email"
            >
              Search
            </button>
          </div>
        </form>
      </div>

      <div className={styles.details}>
        {!submittedEmail ? (
          <div className={styles.noSelection}>
            Type an e-mail above and click Search.
          </div>
        ) : isLoadingCombined ? (
          <LoadingContainer isLoading />
        ) : (
          <Card>
            <Section>
              <header>
                <h2>Accounts associated with email: {submittedEmail}</h2>
              </header>

              <Table>
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>WWG</th>
                    <th>DreamStream</th>
                    <th>Topics</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{submittedEmail}</td>

                    <td>
                      {matchedWWG.length > 0 ? (
                        matchedWWG.map((ibo) => {
                          const { number } = extractIboInfo(ibo);
                          return (
                            <WWGItem
                              key={`wwg-row-${number}`}
                              ibo={ibo}
                              selectedEmail={submittedEmail}
                            />
                          );
                        })
                      ) : (
                        <i>None</i>
                      )}
                    </td>

                    <td>
                      {filteredDreamStream?.length ? (
                        filteredDreamStream.map((ibo) => {
                          const { number, name } = extractIboInfo(ibo);
                          return (
                            <div key={`ds-${number}`}>
                              <Link
                                to={`/membership/lookup/ibo/${number}?tab=dreamstream`}
                              >
                                {name ? `${name} (${number})` : `(${number})`}
                              </Link>
                            </div>
                          );
                        })
                      ) : (
                        <i>None</i>
                      )}
                    </td>

                    <td>
                      {filteredTopics?.length ? (
                        filteredTopics.map((ibo) => {
                          const { number, name } = extractIboInfo(ibo);
                          return (
                            <div key={`tp-${number}`}>
                              <Link
                                to={`/membership/lookup/ibo/${number}?tab=topics`}
                              >
                                {name ? `${name} (${number})` : `(${number})`}
                              </Link>
                            </div>
                          );
                        })
                      ) : (
                        <i>None</i>
                      )}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Section>
          </Card>
        )}
      </div>
    </div>
  );
}
